// Level is the parent component of the actual gameplay. It also serves as a
// game controller of sorts. When the GameStateStore updates with fresh game
// state data, this component will store that data as instructions to
// animate/execute. After this data is saved into state, render will be called
// and the component will place itself into a pre-animation state. Post-render,
// the next animation in the queue will be executed, and once that animation is
// finished, state will be updated to reflect the new state of data/animation.
// If more calls to rebuild state happen while animation is taking place, any
// new animations will be queued.

import { useGameStateContext } from 'app/src/game/GameStateContext';
import { GameFinishedReason } from 'app/src/game/GameStateStore';
import InfoModal from 'app/src/game/InfoModal';
import { InstructionNodeDragType } from 'app/src/game/InstructionNode';
import InstructionsArchiveController from 'app/src/game/InstructionsArchivePanel';
import MapNode from 'app/src/game/map/MapNode';
import ProgramPanel from 'app/src/game/ProgramPanel';
import YouWinModal from 'app/src/game/YouWinModal';
import useEventListener from 'app/src/utils/useEventListener';
import * as React from 'react';
import { useDrop } from 'react-dnd';
import { Link } from 'react-router-dom';

export default function LevelDisplay(): React.ReactElement | null {
	const wrapperRef = React.useRef<HTMLDivElement | null>(null);

	const { gameStateStore, resets, executingProgram, reset, start } = useGameStateContext();

	const [showInfoModal, setShowInfoModal] = React.useState(false);

	// If the user tabs away, reset the game.
	useEventListener('visibilitychange', reset, document);

	// The entire screen should be used as a drop target. This is to work around a
	// limitation of react-dnd where the "return animation" is played when
	// dropping things outside drop targets.
	const [, drop] = useDrop({
		accept: [InstructionNodeDragType],
		drop: (_, monitor) => {
			if (monitor.getDropResult()) {
				return;
			}

			return { handled: false };
		},
	});

	drop(wrapperRef);

	// Decide if the "You Win" modal should be displayed. Make sure we don't show
	// it until after we're done with all animations.
	const showYouWinModal =
		gameStateStore.gameFinishedReason === GameFinishedReason.AllStarsCollected &&
		(gameStateStore.actionResults.length === 0 ||
			gameStateStore.currentActionResultIndex === gameStateStore.actionResults.length);

	return (
		<div className="Level" ref={wrapperRef}>
			<div className="Level-mainPanel">
				<div className="Level-wires" />
				<div className="Level-leftSection">
					<div className="Level-menuButtonsContainer">
						<Link to="/" className="Level-menuButton">
							<div className="Level-menuButtonIcon" />
						</Link>
						<button className="Level-infoButton" onClick={(): void => setShowInfoModal(true)}>
							<div className="Level-infoButtonIcon" />
						</button>
					</div>
					<InstructionsArchiveController />
				</div>
				<div className="Level-rightSection">
					<div className="Level-mapContainer">
						<MapNode key={'resets-' + resets} />
					</div>
					<div className="Level-bottomContainer">
						<ProgramPanel />
						<button className="Level-startButton" onClick={executingProgram ? reset : start}>
							{executingProgram ? 'Reset' : 'Start'}
						</button>
					</div>
				</div>
			</div>
			{showYouWinModal && <YouWinModal />}
			{showInfoModal && <InfoModal onClose={(): void => setShowInfoModal(false)} />}
		</div>
	);
}
